import { z } from 'zod';
import { db } from './db';
import { recordChests } from './gamification';
import { getSupabase, type Db } from './supabase';
import { PermanentSyncError, registerSyncHandler } from './sync-queue';
import type { Database, Json } from './database.types';
import {
  songScoreSchema,
  type Attempt,
  type LessonProgress,
  type Profile,
  type SessionRecord,
  type SkillState,
} from './types';

/**
 * What the outbox actually sends.
 *
 * The queue owns ordering and retries; these handlers own the shape of each
 * call. Everything crossing the wire is validated with zod in both directions:
 * a payload read back from IndexedDB may have been written by an older build,
 * and a response is data from a server that may be ahead of this client.
 */

/* ------------------------------------------------------------ the deltas */

export const streakDeltaSchema = z.object({
  before: z.number().int(),
  after: z.number().int(),
  extended: z.boolean(),
  /** The missed day before this one was the week's rest day. */
  rest_used: z.boolean(),
  lost: z.boolean(),
});

export const sessionDeltasSchema = z.object({
  xp_gained: z.number().int(),
  xp_hour_capped: z.boolean().default(false),
  total_xp: z.number().int(),
  level_before: z.number().int(),
  level_after: z.number().int(),
  /** Picks; the wire keeps the column's name. */
  gems_gained: z.number().int(),
  gems: z.number().int(),
  streak: streakDeltaSchema,
  daily_goal: z.object({
    xp_today: z.number().int(),
    goal: z.number().int(),
    met_now: z.boolean(),
  }),
  quests_progressed: z
    .array(
      z.object({
        quest_id: z.string(),
        before: z.number().int(),
        progress: z.number().int(),
        target: z.number().int(),
        completed: z.boolean(),
        gems: z.number().int(),
      }),
    )
    .default([]),
  achievements: z
    .array(
      z.object({
        achievement_id: z.string(),
        tier: z.number().int(),
        before: z.number().int(),
        progress: z.number().int(),
      }),
    )
    .default([]),
  chests: z.array(z.object({ id: z.string(), kind: z.string(), source: z.string() })).default([]),
  bonds: z
    .array(
      z.object({
        character_id: z.string(),
        points: z.number().int(),
        level: z.number().int(),
      }),
    )
    .default([]),
  crowns: z
    .object({
      lesson_id: z.string(),
      before: z.number().int(),
      after: z.number().int(),
    })
    .nullable()
    .default(null),
});
export type SessionDeltas = z.infer<typeof sessionDeltasSchema>;

/* ------------------------------------------------------- payload -> wire */

/** The queued `session_result` payload, as `applySessionResult` enqueues it. */
const sessionResultPayloadSchema = z.object({
  session: z.custom<SessionRecord>(),
  attempts: z.array(z.custom<Attempt>()),
  skillStates: z.array(z.custom<SkillState>()).default([]),
  lessonProgress: z.custom<LessonProgress>().nullable().default(null),
  /** Songs only; the server's XP for a song is computed from these. */
  stars: z.number().int().min(0).max(3).optional(),
  firstCompletion: z.boolean().optional(),
  /** Gamification. The server re-derives every target and reward from these. */
  section_reached: z.number().int().min(1).default(1),
  quest_set: z.array(z.object({ quest_id: z.string(), difficulty: z.string() })).default([]),
  quest_progress: z.record(z.string(), z.number()).default({}),
  achievement_progress: z.record(z.string(), z.number()).default({}),
  unit_completed: z.boolean().optional(),
  /** Bond points by character; the server adds them to `character_bonds`. */
  bond_points: z.record(z.string(), z.number()).optional(),
});
type SessionResultPayload = z.infer<typeof sessionResultPayloadSchema>;

function toWire(p: SessionResultPayload): Record<string, unknown> {
  const { session, attempts, skillStates, lessonProgress, stars, firstCompletion } = p;
  const { quest_set, quest_progress, achievement_progress, section_reached } = p;
  return {
    session: {
      id: session.id,
      kind: session.kind,
      ref_id: session.refId,
      instrument: session.instrument,
      crown_level: session.crownLevel,
      seed: session.seed,
      started_at: session.startedAt,
      ended_at: session.endedAt,
      exercises: session.exercises,
      correct: session.correct,
      mistakes: session.mistakes,
      max_combo: session.maxCombo,
      accuracy: session.accuracy,
      perfect: session.perfect,
      content_version: session.contentVersion,
      client_version: import.meta.env.VITE_APP_VERSION ?? 'dev',
      ...(stars !== undefined ? { stars } : {}),
      ...(firstCompletion !== undefined ? { first_completion: firstCompletion } : {}),
    },
    attempts: attempts.map((a) => ({
      template_id: a.templateId,
      skill_ids: a.skillIds,
      seed: a.seed,
      difficulty: a.difficulty,
      params_hash: a.paramsHash,
      correct: a.correct,
      score: a.score,
      response_ms: a.responseMs,
      used_fallback: a.usedFallback,
      answered_at: a.answeredAt,
    })),
    skill_states: skillStates.map(skillStateToWire),
    lesson_progress:
      lessonProgress === null
        ? null
        : {
            lesson_id: lessonProgress.lessonId,
            instrument: lessonProgress.instrument,
            crowns: lessonProgress.crowns,
            legendary: lessonProgress.legendary,
            accuracy: lessonProgress.bestAccuracy,
          },
    // The XP the client previewed is deliberately not sent: the server
    // recomputes it, and sending it would only invite trusting it.
    local_date: session.endedAt.slice(0, 10),
    section_reached,
    quest_set,
    quest_progress,
    achievement_progress,
    ...(p.unit_completed === true ? { unit_completed: true } : {}),
    ...(p.bond_points !== undefined ? { bond_points: p.bond_points } : {}),
  };
}

/** The row shape the generated schema expects, minus the owner RLS adds. */
type SkillStateInsert = Omit<Database['public']['Tables']['user_skill_state']['Insert'], 'user_id'>;

function skillStateToWire(row: SkillState): SkillStateInsert {
  return {
    skill_id: row.skillId,
    mastery: row.mastery,
    fsrs_state: row.fsrsState,
    stability: row.stability,
    difficulty: row.difficulty,
    due_at: row.dueAt,
    last_review: row.lastReview,
    reps: row.reps,
    lapses: row.lapses,
    streak: row.streak,
  };
}

/* ------------------------------------------------------------- handlers */

/**
 * The last session's server deltas, and who is waiting for them.
 *
 * Conflict policy: the client's preview is what the completion screen shows
 * first, because the learner should never wait on the network to see their XP.
 * When the server answers, its figures replace the preview — the server is
 * authoritative for everything in the economy, and the two can legitimately
 * differ (a daily cap, an XP boost, a gem roll the client cannot predict). If
 * the answer never arrives, the preview stands and the next sync reconciles the
 * profile; nothing is lost, because the server recomputes from the stored facts.
 */
let lastDeltas: SessionDeltas | null = null;
const listeners = new Set<(deltas: SessionDeltas) => void>();

export function sessionDeltas(): SessionDeltas | null {
  return lastDeltas;
}

/** Called with the deltas for the session that has just been sent. */
export function onSessionDeltas(listener: (deltas: SessionDeltas) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Cleared when a new session starts, so a stale answer cannot bleed into it. */
export function clearSessionDeltas(): void {
  lastDeltas = null;
}

/**
 * The signed-in user's id. Every row written directly carries it, because the
 * RLS policies check `auth.uid() = user_id` on the way in; a row without it is
 * rejected, not silently attributed.
 */
async function currentUserId(supabase: Db): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error !== null) throw new Error(error.message);
  const id = data.session?.user.id;
  if (id === undefined) throw new Error('No session; cannot sync');
  return id;
}

/** Validates a queued payload, marking a bad one as never worth retrying. */
function parseOrDrop<T>(schema: z.ZodType<T>, payload: unknown, kind: string): T {
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new PermanentSyncError(`${kind} payload does not validate: ${parsed.error.message}`);
  }
  return parsed.data;
}

async function sendSessionResult(payload: unknown): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) return;
  const parsed = parseOrDrop(sessionResultPayloadSchema, payload, 'session_result');

  const { data, error } = await supabase.rpc('apply_session_result', {
    p: toWire(parsed) as Json,
  });
  if (error !== null) throw new Error(error.message);

  // The server has accepted it, so the session is synced whatever the response
  // looks like. Marking this after parsing the deltas would let an unexpected
  // shape leave the row looking unsent forever.
  await db.sessions.update(parsed.session.id, { synced: true });

  const deltas = sessionDeltasSchema.safeParse(data);
  if (!deltas.success) {
    // Not worth retrying: the client's preview stays on screen and the next
    // sync reconciles the profile.
    console.warn('[sync] apply_session_result returned an unexpected shape', deltas.error);
    return;
  }
  lastDeltas = deltas.data;
  // Chests are granted by the server, so this is the first the device hears of
  // them; recording them here is what makes them openable.
  await recordChests(deltas.data.chests);
  for (const listener of listeners) listener(deltas.data);
}

async function sendSkillStates(payload: unknown): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) return;
  const rows = parseOrDrop(z.array(z.custom<SkillState>()), payload, 'skill_states');
  if (rows.length === 0) return;
  const userId = await currentUserId(supabase);

  const { error } = await supabase.from('user_skill_state').upsert(
    rows.map((row) => ({ ...skillStateToWire(row), user_id: userId })),
    { onConflict: 'user_id,skill_id' },
  );
  if (error !== null) throw new Error(error.message);

  await db.skillStates.bulkUpdate(
    rows.map((row) => ({ key: row.skillId, changes: { dirty: false } })),
  );
}

async function sendLessonProgress(payload: unknown): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) return;
  const rows = parseOrDrop(z.array(z.custom<LessonProgress>()), payload, 'lesson_progress');
  if (rows.length === 0) return;
  const userId = await currentUserId(supabase);

  const { error } = await supabase.from('user_lesson_progress').upsert(
    rows.map((row) => ({
      user_id: userId,
      lesson_id: row.lessonId,
      instrument: row.instrument,
      crowns: row.crowns,
      legendary: row.legendary,
      times_completed: row.timesCompleted,
      best_accuracy: row.bestAccuracy,
      last_completed_at: row.lastCompletedAt,
    })),
    { onConflict: 'user_id,lesson_id,instrument' },
  );
  if (error !== null) throw new Error(error.message);

  await db.lessonProgress.bulkUpdate(
    rows.map((row) => ({ key: row.key, changes: { dirty: false } })),
  );
}

/**
 * Song scores are upserted whole. The server keeps the best it has seen, so a
 * replay of an older, lower result cannot overwrite a newer, higher one: the
 * row carries the client's best, and the greatest-of is done in the RPC-free
 * way — by never sending anything but the best.
 */
async function sendSongScores(payload: unknown): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) return;
  const rows = parseOrDrop(z.array(songScoreSchema), payload, 'song_score');
  if (rows.length === 0) return;
  const userId = await currentUserId(supabase);

  const { error } = await supabase.from('user_song_scores').upsert(
    rows.map((row) => ({
      user_id: userId,
      song_id: row.songId,
      instrument: row.instrument,
      arrangement: row.arrangement,
      best_score: row.bestScore,
      best_stars: row.bestStars,
      plays: row.plays,
      last_played: row.lastPlayed,
    })),
    { onConflict: 'user_id,song_id,instrument,arrangement' },
  );
  if (error !== null) throw new Error(error.message);

  await db.songScores.bulkUpdate(rows.map((row) => ({ key: row.key, changes: { dirty: false } })));
}

async function sendSettings(payload: unknown): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) return;
  const profile = parseOrDrop(z.custom<Profile>(), payload, 'settings');

  // Only the client-authoritative columns. Economy columns are rejected by a
  // trigger, so sending them would fail the whole update.
  const { error } = await supabase
    .from('profiles')
    .update({
      display_name: profile.displayName,
      companion_character: profile.companionCharacter,
      primary_instrument: profile.primaryInstrument,
      instruments: profile.instruments,
      timezone: profile.timezone,
      daily_goal_xp: profile.dailyGoalXp,
      reminder_hour: profile.reminderHour,
      flags: profile.flags,
      content_version: profile.contentVersion,
    })
    .eq('id', profile.id);
  if (error !== null) throw new Error(error.message);
}

let registered = false;

/** Registers every handler once. Safe to call from more than one entry point. */
export function registerSyncHandlers(): void {
  if (registered) return;
  registered = true;
  registerSyncHandler('session_result', sendSessionResult);
  registerSyncHandler('skill_states', sendSkillStates);
  registerSyncHandler('lesson_progress', sendLessonProgress);
  registerSyncHandler('song_score', sendSongScores);
  registerSyncHandler('settings', sendSettings);
}

export const __testables = { toWire, skillStateToWire };
